import { WrapLgMarginTop } from "App/global-style";
import CollapsibleContainer from "Components/CollapsibleContainer";
import { getOneTimePaymentRadioHint } from "Components/Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/utils";
import { RadioButton } from "Components/Form/RadioButton";
import { NumericOption } from "Components/Form/RadioButton/radioTypes";
import { ThemedButton } from "Components/Themes/components/ThemedButton";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import {
  CoverageNeed,
  ProductCoverageName,
  ProductName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  getRecommended,
  getSelectedCoverageValue,
  isAllowedAmount,
  isCoverageSelected,
} from "Store/akaAdvice/interfaces/getters";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { TextLanguage } from "Store/texts/interfaces";
import { buttonTrackId } from "Utils/analytics";
import { getError } from "Utils/productUtils";
import { FormikProps } from "formik";
import React, { useState } from "react";
import { AkaAdvicePriceFormValues } from "../../../interfaces";
import {
  getDisabilityCapitalCustomerOptions,
  hasDisabilityCapitalCustomerOptions,
} from "./utils";

interface Props {
  formProps: FormikProps<AkaAdvicePriceFormValues>;
  need: CoverageNeed;
  recalculatePrice: (
    formValues: AkaAdvicePriceFormValues,
    specificProduct: ProductName,
  ) => void;
  adviceCard: any;
  lang: TextLanguage;
}

export const DisabilityOneTimePaymentCustomerOptions = ({
  formProps,
  need,
  recalculatePrice,
  adviceCard,
  lang,
}: Props): React.ReactElement | null => {
  const isAdvisor = useIsAdvisorSelector();

  const recommended = getRecommended(
    need,
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
  );
  const isSelected = isCoverageSelected(
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
    formProps.values,
    isAdvisor,
  );
  const selectedValue = getSelectedCoverageValue(
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
    formProps.values,
    isAdvisor,
  );
  const [isOpen, setOpen] = useState(
    isSelected && recommended !== selectedValue,
  );

  const handleOptionChange = (option: NumericOption): void =>
    recalculatePrice(
      {
        ...formProps.values,
        disabilityOneTimePaymentValue: option.value,
      },
      INSURANCE_NAME_VALUE.DISABILITY_FLEX,
    );

  if (
    !formProps.values.disabilityOneTimePaymentCheckbox ||
    !hasDisabilityCapitalCustomerOptions(need)
  ) {
    return null;
  }

  if (
    isAllowedAmount(
      recommended,
      need,
      ProductCoverageName.DISABILITY_FLEX_CAPITAL,
      false,
    )
  ) {
    return (
      <WrapLgMarginTop>
        <ThemedButton
          /**
           * Not great to do labelling this way. Depends on internal Storeblocks logic.
           * Storeblocks Radio sets radio group to be labelled by element with id "{id}-radio-label"
           * Vulnerable to changes by storeblocks.
           * Would ideally use aria-labelledby on the Radio component,
           * but that messes up other storeblocks labelling functionality.
           */
          id="disabilityOneTimePaymentValue-radio-label"
          trackId={buttonTrackId("disability-capital-options-accordion")}
          type="button"
          variant="text"
          onClick={() => setOpen(!isOpen)}
          paddingLeft={0}
          iconRight={isOpen ? "chevron-up" : "chevron-down"}
        >
          {
            adviceCard.disabilityOneTimePayment.options.chooseDifferentHeader[
              lang
            ]
          }
        </ThemedButton>
        <CollapsibleContainer isOpen={isOpen}>
          <RadioButton
            id="disabilityOneTimePaymentValue"
            name="disabilityOneTimePaymentValue"
            hint={getOneTimePaymentRadioHint(need, adviceCard, lang)}
            options={getDisabilityCapitalCustomerOptions(need)}
            onChange={handleOptionChange}
            error={getError("disabilityOneTimePaymentValue", formProps)}
            numeric
            marginBottom="0px"
          />
        </CollapsibleContainer>
      </WrapLgMarginTop>
    );
  }

  return (
    <WrapLgMarginTop>
      <RadioButton
        id="disabilityOneTimePaymentValue"
        name="disabilityOneTimePaymentValue"
        label={adviceCard.disabilityOneTimePayment.options.chooseHeader[lang]}
        options={getDisabilityCapitalCustomerOptions(need)}
        onChange={handleOptionChange}
        error={getError("disabilityOneTimePaymentValue", formProps)}
        numeric
        marginBottom="0px"
      />
    </WrapLgMarginTop>
  );
};
